import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, fmtINR } from "../lib/api";
import { Printer } from "lucide-react";

export default function PrintInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const mode = params.get("mode") || "invoice"; // invoice | packing
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}/invoice`).then((r) => setData(r.data)).catch(() => setData(undefined));
  }, [id]);

  useEffect(() => { document.title = `${mode === "packing" ? "Packing Slip" : "Tax Invoice"} — Joy Pearl`; }, [mode]);

  if (data === undefined) return <div className="p-10 text-center">Not found</div>;
  if (!data) return <div className="p-10 text-center text-brand-muted">Loading…</div>;

  const { order, invoice_number, seller, items, tax_summary, grand_total } = data;
  const addr = order.address || {};

  return (
    <div className="bg-white min-h-screen text-brand-ink p-6 md:p-10" data-testid="print-page">
      <style>{`
        @media print {
          @page { size: A4; margin: 14mm; }
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between border-b border-brand-line pb-6">
          <div>
            <p className="font-serif text-3xl tracking-tight">Joy Pearl</p>
            <p className="text-xs text-brand-muted mt-1">vskstore.com</p>
          </div>
          <div className="text-right">
            <h1 className="font-serif text-2xl">{mode === "packing" ? "Packing Slip" : "Tax Invoice"}</h1>
            <p className="text-sm mt-1">Invoice #: <span className="font-mono">{invoice_number}</span></p>
            <p className="text-sm">Order #: <span className="font-mono">{order.order_number}</span></p>
            <p className="text-xs text-brand-muted">Date: {new Date(order.created_at).toLocaleDateString("en-IN")}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-6 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Sold by</p>
            <p className="font-medium">{seller.name}</p>
            <p className="text-brand-muted">{seller.address}</p>
            <p className="text-brand-muted text-xs mt-1">GSTIN: <span className="font-mono">{seller.gstin}</span></p>
            <p className="text-brand-muted text-xs">State: {seller.state}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">{mode === "packing" ? "Ship to" : "Bill to"}</p>
            <p className="font-medium">{order.customer_name}</p>
            <p className="text-brand-muted text-xs">+91 {addr.phone}</p>
            <p className="text-brand-muted mt-1">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
            <p className="text-brand-muted">{addr.city}, {addr.state} {addr.pincode}</p>
          </div>
        </div>

        <table className="w-full mt-8 text-sm border-t border-brand-line">
          <thead>
            <tr className="border-b border-brand-line text-xs uppercase tracking-wider">
              <th className="text-left p-2">Item</th>
              <th className="text-left p-2">HSN</th>
              <th className="text-right p-2">Qty</th>
              {mode !== "packing" && <>
                <th className="text-right p-2">Price</th>
                <th className="text-right p-2">Taxable</th>
                <th className="text-right p-2">GST</th>
              </>}
              <th className="text-right p-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-b border-brand-line">
                <td className="p-2">{it.name}</td>
                <td className="p-2 font-mono text-xs">{it.hsn_code}</td>
                <td className="p-2 text-right">{it.qty}</td>
                {mode !== "packing" && <>
                  <td className="p-2 text-right">{fmtINR(it.price)}</td>
                  <td className="p-2 text-right">{fmtINR(it.taxable_value)}</td>
                  <td className="p-2 text-right">{fmtINR(it.tax)}</td>
                </>}
                <td className="p-2 text-right font-medium">{fmtINR(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {mode !== "packing" && (
          <div className="mt-6 grid grid-cols-2 gap-6 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-2">Tax summary</p>
              <table className="w-full text-xs">
                <tbody>
                  <tr><td className="py-1 text-brand-muted">Taxable value</td><td className="text-right">{fmtINR(tax_summary.taxable_value)}</td></tr>
                  {tax_summary.intrastate ? <>
                    <tr><td className="py-1 text-brand-muted">CGST ({(tax_summary.gst_rate/2).toFixed(1)}%)</td><td className="text-right">{fmtINR(tax_summary.cgst)}</td></tr>
                    <tr><td className="py-1 text-brand-muted">SGST ({(tax_summary.gst_rate/2).toFixed(1)}%)</td><td className="text-right">{fmtINR(tax_summary.sgst)}</td></tr>
                  </> : (
                    <tr><td className="py-1 text-brand-muted">IGST ({tax_summary.gst_rate}%)</td><td className="text-right">{fmtINR(tax_summary.igst)}</td></tr>
                  )}
                  <tr><td className="py-1 text-brand-muted">Total GST</td><td className="text-right font-medium">{fmtINR(tax_summary.total_gst)}</td></tr>
                </tbody>
              </table>
            </div>
            <div>
              <table className="w-full text-sm">
                <tbody>
                  <tr><td className="py-1 text-brand-muted">Subtotal</td><td className="text-right">{fmtINR(order.subtotal)}</td></tr>
                  <tr><td className="py-1 text-brand-muted">Shipping</td><td className="text-right">{order.shipping === 0 ? "Free" : fmtINR(order.shipping)}</td></tr>
                  {order.discount > 0 && <tr><td className="py-1 text-brand-muted">Discount {order.coupon_code && `(${order.coupon_code})`}</td><td className="text-right text-emerald-700">− {fmtINR(order.discount)}</td></tr>}
                  <tr><td className="py-1 text-brand-muted">COD handling</td><td className="text-right">{fmtINR(order.cod_fee || 0)}</td></tr>
                  <tr className="border-t border-brand-line"><td className="py-2 font-serif text-lg">Grand total</td><td className="text-right font-serif text-lg" data-testid="invoice-total">{fmtINR(grand_total)}</td></tr>
                  <tr><td colSpan={2} className="py-1 text-right text-xs text-brand-muted">Paid via Cash on Delivery</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-brand-line text-xs text-brand-muted">
          <p>Thank you for shopping with Joy Pearl. For returns or assistance, message <a href="https://www.instagram.com/joypearl_official">@joypearl_official</a> or email joypearlvske@gmail.com within 7 days of delivery.</p>
          <p className="mt-2">This is a computer-generated document and does not require a physical signature.</p>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 no-print">
          <button onClick={async () => {
            try {
              const r = await api.get(`/orders/${id}/${mode === "packing" ? "packing-slip-pdf" : "invoice-pdf"}`, { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = `JoyPearl-${mode === "packing" ? "PackingSlip" : "Invoice"}-${data.order.order_number}.pdf`;
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (e) {
              alert(e?.response?.data?.detail || "PDF download failed");
            }
          }} className="jp-btn-primary" data-testid="download-pdf-button">
            <Printer size={14} className="mr-2"/>Download PDF (A4)
          </button>
          <button onClick={() => window.print()} className="jp-btn-outline" data-testid="print-button">
            <Printer size={14} className="mr-2"/>Print preview
          </button>
        </div>
      </div>
    </div>
  );
}
